export type BufferFlushReason =
  | 'max_chunks'
  | 'table_row_complete'
  | 'timeout'
  | 'stream_end'
  | 'manual'

export interface StreamChunkBufferOptions {
  minChunks: number
  maxChunks: number
  flushTimeoutMs?: number
  enableTableAwareFlush?: boolean
}

export interface BufferFlushResult {
  text: string
  chunkCount: number
  reason: BufferFlushReason
}

export interface StreamChunkBuffer {
  push(chunk: string): BufferFlushResult | null
  flush(reason?: BufferFlushReason): BufferFlushResult | null
  reset(): void
  getPendingChunkCount(): number
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim()
  if (!trimmed) return false

  const pipeCount = (trimmed.match(/\|/g) || []).length
  if (pipeCount < 2) return false

  if (!trimmed.startsWith('|') && !trimmed.endsWith('|')) return false

  return true
}

function normalizeDividerDashes(line: string): string {
  return line.replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
}

function isTableDivider(line: string): boolean {
  if (!isTableRow(line)) return false

  const trimmed = line.trim()
  const content =
    trimmed.startsWith('|') && trimmed.endsWith('|') ? trimmed.slice(1, -1) : trimmed

  const cells = content.split('|').map((cell) => cell.trim())

  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?[-\u2010\u2011\u2012\u2013\u2014\u2212]+:?$/.test(cell))
  )
}

const TABLE_MAX_CHUNK_OVERFLOW_LIMIT = 40

function endsWithIncompleteTableRow(text: string): boolean {
  const lastNewlineIndex = text.lastIndexOf('\n')
  const trailingLine = lastNewlineIndex === -1 ? text : text.slice(lastNewlineIndex + 1)
  if (!trailingLine.trim()) return false
  return isTableRow(trailingLine) && !text.endsWith('\n')
}

function splitMergedDividerAndRow(line: string): string[] {
  if (!/\|\s*\|/.test(line)) {
    return [line]
  }

  const boundaryRe = /\|\s*\|/g
  let match: RegExpExecArray | null
  while ((match = boundaryRe.exec(line)) !== null) {
    const firstPipeIndex = match.index
    const secondPipeIndex = match.index + match[0].length - 1

    const left = line.slice(0, firstPipeIndex + 1)
    const right = line.slice(secondPipeIndex)

    if (isTableDivider(left) && isTableRow(right)) {
      return [normalizeDividerDashes(left), right]
    }
  }

  return [line]
}

export function createStreamChunkBuffer(opts: StreamChunkBufferOptions): StreamChunkBuffer {
  const { minChunks, maxChunks, flushTimeoutMs = 1000, enableTableAwareFlush = true } = opts

  let pendingChunks: string[] = []
  let pendingText = ''
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let inCodeFence = false
  let tableState: 'none' | 'header_seen' | 'table_confirmed' = 'none'
  let lastProcessedLineCount = 0
  let tableStateProcessedLineCount = 0

  function normalizePendingTextForTables(): void {
    if (!pendingText.includes('|')) return
    if (!/\|\s*\|/.test(pendingText) && !/[\u2010\u2011\u2012\u2013\u2014\u2212]/.test(pendingText)) {
      return
    }

    const lines = pendingText.split('\n')
    const out: string[] = []

    for (const line of lines) {
      if (!line) {
        out.push(line)
        continue
      }

      const split = splitMergedDividerAndRow(line)
      if (split.length > 1) {
        out.push(...split)
        continue
      }

      if (isTableDivider(line)) {
        out.push(normalizeDividerDashes(line))
        continue
      }

      out.push(line)
    }

    pendingText = out.join('\n')
  }

  function clearFlushTimeout(): void {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  function startFlushTimeout(onTimeout: () => void): void {
    clearFlushTimeout()
    if (flushTimeoutMs > 0) {
      timeoutId = setTimeout(onTimeout, flushTimeoutMs)
    }
  }

  function shouldFlushForTableRow(): boolean {
    if (!enableTableAwareFlush) return false
    if (pendingChunks.length < minChunks) return false

    const lastNewlineIndex = pendingText.lastIndexOf('\n')
    if (lastNewlineIndex === -1) return false

    const lines = pendingText.substring(0, lastNewlineIndex + 1).split('\n')
    if (lines.length === 0) return false

    for (let i = lastProcessedLineCount; i < lines.length; i += 1) {
      const line = lines[i]
      if (line !== undefined) {
        const trimmed = line.trim()
        if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
          inCodeFence = !inCodeFence
        }
      }
    }
    lastProcessedLineCount = lines.length

    if (inCodeFence) return false

    const completeLineCount = lines.length - 1
    let shouldFlush = false

    for (let i = tableStateProcessedLineCount; i < completeLineCount; i += 1) {
      const line = lines[i]
      if (!line) continue

      if (tableState === 'none') {
        if (isTableRow(line) && !isTableDivider(line)) {
          tableState = 'header_seen'
        }
      } else if (tableState === 'header_seen') {
        if (isTableDivider(line)) {
          tableState = 'table_confirmed'
        } else if (isTableRow(line) && !isTableDivider(line)) {
          // Keep looking for divider after header.
        } else {
          tableState = 'none'
        }
      } else if (tableState === 'table_confirmed') {
        if (isTableRow(line) && !isTableDivider(line)) {
          shouldFlush = true
          break
        }
        tableState = 'none'
      }
    }

    tableStateProcessedLineCount = completeLineCount

    return shouldFlush
  }

  function shouldDeferMaxChunkFlush(): boolean {
    if (!enableTableAwareFlush) return false
    if (pendingChunks.length >= TABLE_MAX_CHUNK_OVERFLOW_LIMIT) return false

    return tableState === 'table_confirmed' && endsWithIncompleteTableRow(pendingText)
  }

  function executeFlush(reason: BufferFlushReason): BufferFlushResult | null {
    if (pendingChunks.length === 0) return null

    const result: BufferFlushResult = {
      text: pendingText,
      chunkCount: pendingChunks.length,
      reason,
    }

    pendingChunks = []
    pendingText = ''
    inCodeFence = false
    tableState = 'none'
    lastProcessedLineCount = 0
    tableStateProcessedLineCount = 0
    clearFlushTimeout()

    return result
  }

  return {
    push(chunk: string): BufferFlushResult | null {
      pendingChunks.push(chunk)
      pendingText += chunk
      normalizePendingTextForTables()

      if (pendingChunks.length === 1) {
        startFlushTimeout(() => {
          // Consumer controls timeout flush policy.
        })
      }

      // Check for table-aware flush first.
      if (shouldFlushForTableRow()) {
        return executeFlush('table_row_complete')
      }

      // Force flush at maxChunks unless we're in the middle of a confirmed table row.
      // Let the row finish so markdown-it can parse the table incrementally.
      if (pendingChunks.length >= maxChunks) {
        if (shouldDeferMaxChunkFlush()) {
          return null
        }
        return executeFlush('max_chunks')
      }

      if (pendingChunks.length < minChunks) {
        return null
      }

      return null
    },

    flush(reason: BufferFlushReason = 'manual'): BufferFlushResult | null {
      return executeFlush(reason)
    },

    reset(): void {
      pendingChunks = []
      pendingText = ''
      inCodeFence = false
      tableState = 'none'
      lastProcessedLineCount = 0
      tableStateProcessedLineCount = 0
      clearFlushTimeout()
    },

    getPendingChunkCount(): number {
      return pendingChunks.length
    },
  }
}

